import { useEffect } from "react";
import { useLocale } from "@/context/LocaleContext";
import { FOUNDATION } from "@/data/content";

/**
 * SEO metadata for each page (native document.head manipulation for React 19 compatibility).
 * Props:
 *  - title (string or {id, en})
 *  - description (string or {id, en})
 *  - image (optional URL)
 *  - path (optional URL path)
 *  - type ('website' | 'article')
 */
export default function SeoHead({ title, description, image, path, type = "website" }) {
  const { pick, locale } = useLocale();

  useEffect(() => {
    if (typeof document === "undefined") return;

    const pageTitle = typeof title === "string" ? title : pick(title);
    const pageDesc = typeof description === "string" ? description : pick(description);
    const fullTitle = pageTitle
      ? `${pageTitle} · ${FOUNDATION.shortName}`
      : `${FOUNDATION.shortName} — ${FOUNDATION.taglineSundanese}`;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";
    const url = origin + (path || currentPath);
    const imgUrl = image
      ? (image.startsWith("http") ? image : origin + image)
      : `${origin}/assets/sauyunan-logo.png`;

    document.title = fullTitle;
    document.documentElement.lang = locale;

    const upsert = (selector, tagName, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(tagName);
        el.setAttribute("data-sf-seo", "true");
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => {
        if (v == null || v === "") return;
        el.setAttribute(k, v);
      });
      return el;
    };

    const meta = (attr, value, content) => {
      upsert(`meta[${attr}="${value}"]`, "meta", { [attr]: value, content });
    };
    const link = (rel, href) => {
      upsert(`link[rel="${rel}"]`, "link", { rel, href });
    };

    meta("name", "description", pageDesc);
    // Open Graph
    meta("property", "og:type", type);
    meta("property", "og:site_name", FOUNDATION.shortName);
    meta("property", "og:title", fullTitle);
    meta("property", "og:description", pageDesc);
    meta("property", "og:url", url);
    meta("property", "og:image", imgUrl);
    meta("property", "og:locale", locale === "id" ? "id_ID" : "en_US");
    // Twitter
    meta("name", "twitter:card", "summary_large_image");
    meta("name", "twitter:title", fullTitle);
    meta("name", "twitter:description", pageDesc);
    meta("name", "twitter:image", imgUrl);
    // Canonical
    link("canonical", url);

    return () => {
      // do not remove - keep last valid tags to avoid flicker between routes
    };
  }, [title, description, image, path, type, locale, pick]);

  return null;
}
